import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";
import { BookOpen, Plus, FileText, Eye, EyeOff } from "lucide-react";

const STATUS_PILLS = {
  draft:     "bg-navy-100 text-navy-500",
  published: "bg-green-100 text-green-700",
};

export default function SuperAdminKb() {
  const { formatDate } = useLocale();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/kb/articles", { params: { include_drafts: true, limit: 100 } }),
        api.get("/kb/categories"),
      ]);
      setArticles(a.data.items || []);
      setCategories(c.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = articles.filter((a) => {
    if (filter === "all") return true;
    return a.status === filter;
  });

  const publish = async (a) => {
    try {
      await api.patch(`/super-admin/kb/articles/${a.slug}`, { ...a, status: a.status === "published" ? "draft" : "published" });
      await load();
      toast.success(a.status === "published" ? "Article unpublished" : "Article published");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/super-admin/kb/articles/${a.slug}`);
      await load();
      toast.success("Article deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Knowledge base (Super admin)">
      <div className="max-w-6xl mx-auto py-8 px-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-semibold uppercase tracking-wider mb-1">
              <BookOpen className="w-3 h-3" /> Super admin
            </div>
            <h1 className="text-2xl font-semibold" data-testid="super-kb-heading">Knowledge base</h1>
            <p className="text-sm text-navy-500 mt-0.5">Author, edit, publish and delete platform-wide articles. Visible to every customer once published.</p>
          </div>
          <Link to="/super-admin/kb/new" className="btn-accent inline-flex items-center gap-1.5" data-testid="super-kb-new">
            <Plus className="w-4 h-4" /> New article
          </Link>
        </div>

        <div className="eb-card p-3 mb-3 flex items-center gap-2" data-testid="super-kb-filters">
          {["all", "published", "draft"].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs px-2 py-1 rounded ${filter === s ? "bg-navy-900 text-white" : "border border-navy-200"}`}
              data-testid={`super-kb-filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <span className="text-xs text-navy-500 ml-auto">{categories.length} categor{categories.length === 1 ? "y" : "ies"}</span>
        </div>

        {loading ? (
          <div className="text-xs text-navy-400">Loading…</div>
        ) : (
          <div className="eb-card p-0 overflow-hidden" data-testid="super-kb-list">
            <table className="w-full text-sm">
              <thead className="bg-navy-50/40 text-[11px] uppercase tracking-wider text-navy-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Title</th>
                  <th className="text-left px-4 py-2.5">Category</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">Updated</th>
                  <th className="text-left px-4 py-2.5">Views</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-t border-navy-100" data-testid={`super-kb-row-${a.slug}`}>
                    <td className="px-4 py-2.5">
                      <Link to={`/super-admin/kb/${a.slug}/edit`} className="font-medium text-navy-900 dark:text-white hover:text-orange-600">
                        {a.title}
                      </Link>
                      <div className="text-[10px] text-navy-400">/{a.slug}</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-navy-500">{a.category}</td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_PILLS[a.status] || ""}`} data-testid={`super-kb-status-${a.slug}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-navy-500">{a.updated_at ? formatDate(a.updated_at) : "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-navy-500">{a.views}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => publish(a)}
                        className="text-xs px-2 py-1 rounded border border-navy-200 hover:bg-navy-50 mr-1.5"
                        data-testid={`super-kb-toggle-${a.slug}`}
                      >
                        {a.status === "published" ? <><EyeOff className="w-3 h-3 inline mr-1" />Unpublish</> : <><Eye className="w-3 h-3 inline mr-1" />Publish</>}
                      </button>
                      <button
                        onClick={() => remove(a)}
                        className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`super-kb-delete-${a.slug}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AppShell>
  );
}
